import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import {
  Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableHead, TableRow, TableCell, TableBody, Chip, Box,
  IconButton, Tooltip, MenuItem
} from "@mui/material";
import AddIcon        from "@mui/icons-material/Add";
import OpenInNewIcon  from "@mui/icons-material/OpenInNew";
import DeleteIcon     from "@mui/icons-material/Delete";
import { useAuth }    from "../components/AuthContext";

const ANIO_ACTUAL = new Date().getFullYear();

const ESTADO_CONFIG = {
  borrador:  { label: "Draft",        color: "default" },
  aprobado:  { label: "Approved",     color: "info"    },
  en_curso:  { label: "In progress",  color: "warning" },
  cerrado:   { label: "Closed",       color: "success" },
};

const EMPTY_NEW = { nombre: "", anio: ANIO_ACTUAL, objetivo: "", fecha_inicio: "", fecha_fin: "" };

export default function Planes() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const puedeEditar = hasPermission("PLANES_EDITAR");

  const [planes, setPlanes]         = useState([]);
  const [filtroAnio, setFiltroAnio] = useState("");
  const [openNew, setOpenNew]       = useState(false);
  const [newPlan, setNewPlan]       = useState(EMPTY_NEW);
  const [confirmDel, setConfirmDel] = useState(null);

  const cargar = useCallback(async () => {
    const params = {};
    if (filtroAnio) params.anio = filtroAnio;
    const res = await api.get("/planes", { params });
    setPlanes(res.data);
  }, [filtroAnio]);

  useEffect(() => { cargar(); }, [cargar]);

  const crear = async () => {
    if (!newPlan.nombre.trim()) return alert("The name is required");
    try {
      const body = {
        nombre:       newPlan.nombre.trim(),
        anio:         parseInt(newPlan.anio),
        objetivo:     newPlan.objetivo.trim() || null,
        fecha_inicio: newPlan.fecha_inicio || null,
        fecha_fin:    newPlan.fecha_fin    || null,
      };
      const res = await api.post("/planes", body);
      setOpenNew(false);
      setNewPlan(EMPTY_NEW);
      navigate(`/planes/${res.data.id_plan}`);
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const eliminar = async () => {
    try {
      await api.delete(`/planes/${confirmDel.id_plan}`);
      setConfirmDel(null);
      cargar();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const aniosDisponibles = [...new Set(planes.map(p => p.anio))].sort((a, b) => b - a);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Training Plans
        </Typography>

        <TextField
          select label="Year" size="small" value={filtroAnio}
          onChange={e => setFiltroAnio(e.target.value)}
          sx={{ minWidth: 110 }}
        >
          <MenuItem value="">All</MenuItem>
          {aniosDisponibles.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </TextField>

        {puedeEditar && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenNew(true)}>
            New Plan
          </Button>
        )}
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#000" }}>
            <TableCell sx={{ color: "white" }}>Plan</TableCell>
            <TableCell sx={{ color: "white" }}>Year</TableCell>
            <TableCell sx={{ color: "white" }}>Status</TableCell>
            <TableCell sx={{ color: "white" }}>Courses</TableCell>
            <TableCell sx={{ color: "white" }}>Validity</TableCell>
            <TableCell sx={{ color: "white" }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {planes.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                No plans registered
              </TableCell>
            </TableRow>
          )}
          {planes.map(p => {
            const cfg = ESTADO_CONFIG[p.estado] || { label: p.estado, color: "default" };
            return (
              <TableRow key={p.id_plan} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.nombre}</Typography>
                  {p.objetivo && (
                    <Typography variant="caption" color="text.secondary">
                      {p.objetivo.length > 80 ? p.objetivo.slice(0, 80) + "…" : p.objetivo}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{p.anio}</TableCell>
                <TableCell>
                  <Chip label={cfg.label} color={cfg.color} size="small" />
                </TableCell>
                <TableCell>{p.num_cursos}</TableCell>
                <TableCell>
                  {p.fecha_inicio || p.fecha_fin
                    ? <Typography variant="body2">{p.fecha_inicio || "—"} → {p.fecha_fin || "—"}</Typography>
                    : <Typography variant="body2" color="text.secondary">—</Typography>
                  }
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="View details">
                      <IconButton size="small" color="primary" onClick={() => navigate(`/planes/${p.id_plan}`)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {puedeEditar && ["borrador", "aprobado"].includes(p.estado) && (
                      <Tooltip title="Delete plan">
                        <IconButton size="small" color="error" onClick={() => setConfirmDel(p)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* ── New plan ── */}
      <Dialog open={openNew} onClose={() => setOpenNew(false)} fullWidth maxWidth="sm">
        <DialogTitle>New Training Plan</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Plan name *" fullWidth
            value={newPlan.nombre}
            onChange={e => setNewPlan({ ...newPlan, nombre: e.target.value })}
          />
          <TextField
            label="Year *" type="number" fullWidth
            value={newPlan.anio}
            onChange={e => setNewPlan({ ...newPlan, anio: e.target.value })}
            inputProps={{ min: 2000, max: 2100 }}
          />
          <TextField
            label="Objective" fullWidth multiline rows={3}
            value={newPlan.objetivo}
            onChange={e => setNewPlan({ ...newPlan, objetivo: e.target.value })}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Start date" type="date" fullWidth
              InputLabelProps={{ shrink: true }}
              value={newPlan.fecha_inicio}
              onChange={e => setNewPlan({ ...newPlan, fecha_inicio: e.target.value })}
            />
            <TextField
              label="End date" type="date" fullWidth
              InputLabelProps={{ shrink: true }}
              value={newPlan.fecha_fin}
              onChange={e => setNewPlan({ ...newPlan, fecha_fin: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenNew(false); setNewPlan(EMPTY_NEW); }}>Cancel</Button>
          <Button variant="contained" onClick={crear}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm deletion ── */}
      <Dialog open={!!confirmDel} onClose={() => setConfirmDel(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete plan</DialogTitle>
        <DialogContent>
          <Typography>
            Delete the plan <strong>{confirmDel?.nombre}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={eliminar}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
